import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth.js';
import { Nav } from './components/Nav.jsx';
import { LoginPage } from './routes/Login.jsx';
import { RegisterPage } from './routes/Register.jsx';
import { ForgotPasswordPage } from './routes/ForgotPassword.jsx';
import { ResetPasswordPage } from './routes/ResetPassword.jsx';
import { DashboardPage } from './routes/Dashboard.jsx';
import { PropertiesPage } from './routes/Properties.jsx';
import { PropertyDetailPage } from './routes/PropertyDetail.jsx';
import { TenantsPage } from './routes/Tenants.jsx';
import { TenantDetailPage } from './routes/TenantDetail.jsx';
import { RentPage } from './routes/Rent.jsx';
import { ExpensesPage } from './routes/Expenses.jsx';
import { AssetsPage } from './routes/Assets.jsx';
import { IncomePage } from './routes/Income.jsx';
import { ReportsPage } from './routes/Reports.jsx';
import { ElectricityPage } from './routes/Electricity.jsx';
import { NotificationsPage } from './routes/Notifications.jsx';
import { InvitationsPage } from './routes/Invitations.jsx';
import { SetupPage } from './routes/Setup.jsx';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/forgot" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/reset" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
          <Route element={<PrivateShell />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertyDetailPage />} />
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/rent" element={<RentPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/income" element={<IncomePage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/electricity" element={<ElectricityPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/invitations" element={<InvitationsPage />} />
            <Route path="/setup" element={<SetupPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function PrivateShell() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return (<><Nav /><Outlet /></>);
}
function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}
function Loading() {
  return <div className="mx-auto mt-24 text-center text-slate-500">Loading…</div>;
}
